/* eslint-disable eqeqeq */

import type { LineItem, Cart } from '../types/cart'
import type { Product } from '../types/product'
import type {
  Cart as ShopifyCart,
  Product as ShopifyProduct,
  MoneyV2,
  ImageConnection,
  ProductVariantConnection,
  SelectedOption,
  ProductOption,
  SellingPlanConnection,
  SellingPlanGroupConnection,
} from '../schema'
import { colorMap } from './colors'

type KeyValuePair = Record<string, any>

function normalizeMerchandise(merchandise: KeyValuePair): LineItem {
  const { node } = merchandise
  const {
    id,
    quantity,
    attributes,
    cost,
    sellingPlanAllocation,
    merchandise: variant,
  } = node

  const { sellingPlan = {} } = sellingPlanAllocation || {}
  return {
    id,
    quantity,
    attributes,
    sellingPlanId: sellingPlan.id || null,
    variantId: String(variant?.id),
    productId: String(variant?.product.id),
    name: variant?.product.title,
    path: String(variant?.product.handle),
    discounts: [],
    options: variant?.title == 'Default Title' ? [] : variant?.selectedOptions,
    totalAmount: Number(cost.totalAmount.amount),
    variant: {
      id: String(variant?.id),
      sku: variant?.sku || '',
      name: variant?.title!,
      image: {
        url: variant?.image?.url,
      },
      requiresShipping: variant?.requiresShipping || false,
      price: variant?.price?.amount,
      listPrice: variant?.compareAtPrice?.amount,
    },
  }
}

export function normalizeShopifyCart(cart: ShopifyCart): Cart {
  return {
    id: cart.id,
    url: cart.checkoutUrl,
    customerId: '',
    email: '',
    createdAt: cart.createdAt,
    currency: {
      code: cart.cost.totalAmount.currencyCode,
    },
    taxesIncluded: false,
    lineItems: cart.lines?.edges.map(normalizeMerchandise),
    lineItemsSubtotalPrice: +cart.cost.subtotalAmount?.amount,
    subtotalPrice: +cart.cost.subtotalAmount?.amount,
    totalPrice: cart.cost.totalAmount?.amount,
    discounts: [],
  }
}

const money = ({ amount, currencyCode }: MoneyV2) => {
  return {
    value: +amount,
    currencyCode,
  }
}

const normalizeProductImages = ({ edges }: ImageConnection) =>
  edges?.map(({ node: { url, ...rest } }) => ({
    url,
    ...rest,
  }))

const normalizeProductVariants = ({ edges }: ProductVariantConnection) => {
  return edges?.map(
    ({
      node: {
        id,
        selectedOptions,
        sku,
        title,
        priceV2,
        compareAtPriceV2,
        requiresShipping,
        availableForSale,
        image,
      },
    }) => {
      return {
        id,
        name: title,
        sku,
        price: +priceV2.amount,
        listPrice: +compareAtPriceV2?.amount,
        requiresShipping,
        availableForSale,
        image,
        options: selectedOptions.map(({ name, value }: SelectedOption) => {
          const options = normalizeProductOption({
            id,
            name,
            values: [value],
          })

          return options
        }),
      }
    }
  )
}

const normalizeProductOption = ({
  id,
  name: displayName,
  values,
}: ProductOption) => {
  return {
    __typename: 'MultipleChoiceOption',
    id,
    displayName: displayName.toLowerCase(),
    values: values.map((value) => {
      let output: any = {
        label: value,
      }
      if (displayName.match(/colou?r/gi)) {
        const mapedColor = colorMap[value.toLowerCase().replace(/ /g, '')]
        if (mapedColor) {
          output = {
            ...output,
            hexColors: [mapedColor],
          }
        }
      }
      return output
    }),
  }
}

const normalizeProductSellingPlans = (sellingPlans: SellingPlanConnection) => {
  const { edges = [] } = sellingPlans
  const normalizedSellingPlans = edges.map((edge: Record<string, any> = {}) => {
    const { node } = edge
    return node
  })

  return normalizedSellingPlans
}

const normalizeProductSellingPlanGroups = (
  sellingPlanGroups: SellingPlanGroupConnection
) => {
  const { edges = [] } = sellingPlanGroups
  const normalizedSellingPlanGroups = edges.map(
    (edge: Record<string, any> = {}) => {
      const { node } = edge
      return {
        name: node.name,
        options: node.options,
        sellingPlans: normalizeProductSellingPlans(node.sellingPlans),
      }
    }
  )

  return normalizedSellingPlanGroups
}

const normalizeSize = (options: Record<string, any>) => {
  const sizeOptions: string[] = []
  if (options?.length && options.length > 0) {
    options.forEach((option: any) => {
      if (option?.name?.toLowerCase().includes('size')) {
        const valueList = ('' + option.values).split(',')
        valueList.forEach((optionValue: string) => {
          if (sizeOptions.indexOf(optionValue) === -1) {
            sizeOptions.push(optionValue)
          }
        })
      }
    })
  }
  return sizeOptions
}

const normalizeInfoValue = (infoPoint: Record<string, any>) => {
  return infoPoint?.value ? JSON.parse(infoPoint.value) : []
}

const normalizeOption = (options: Record<string, any>, optionName: string) => {
  let optionValues: any[] = []
  if (options?.length && options.length > 0) {
    options.forEach((option: any) => {
      if (option.name.toLowerCase() === optionName) {
        optionValues = option.values
      }
    })
  }
  return optionValues
}

const getPriceTag = (product: Record<string, any>) => {
  const price = product?.priceRange?.minVariantPrice?.amount | 0

  if (price > 0 && price < 20) {
    return ['Under $20']
  } else if (price >= 20 && price < 40) {
    return ['$20 to $40']
  } else if (price >= 40) {
    return ['$40+']
  } else {
    return []
  }
}

const normalizeInfo = (args: Record<string, any>) => {
  return {
    healthNeeds: normalizeInfoValue(args?.healthNeedsRaw),
    productForm: normalizeInfoValue(args?.productFormRaw),
    ingredients: normalizeInfoValue(args?.ingredientsRaw),
    size: normalizeSize(args?.options),
    count: normalizeOption(args?.options, 'count'),
    scent: normalizeOption(args?.options, 'scent'),
    flavor: normalizeOption(args?.options, 'flavors'),
    trimmerType: normalizeInfoValue(args?.trimmerType),
    price: getPriceTag(args),
    new: args?.isNew?.value || false,
    bestseller: args?.isBestseller?.value ? ['Bestseller'] : [],
    bundle: args?.isBundle?.value ? ['Bundle'] : [],
  }
}

const checkAvailable = (variants: any) => {
  return variants.edges.reduce((acc: boolean, edge: any) => {
    return acc || edge.node.availableForSale
  }, false)
}

export function normalizeProduct({
  id,
  title: name,
  vendor,
  images,
  variants,
  description,
  descriptionHtml,
  handle,
  priceRange,
  options,
  metafields,
  sellingPlanGroups,
  collections,
  compareAtPriceRange,
  ...rest
}: ShopifyProduct): Product {
  return {
    id,
    name,
    vendor,
    path: `/${handle}`,
    slug: handle?.replace(/^\/+|\/+$/g, ''),
    price: money(priceRange?.minVariantPrice),
    images: normalizeProductImages(images),
    image: normalizeProductImages(images)[0],
    variants: variants ? normalizeProductVariants(variants) : [],
    sellingPlanGroups: normalizeProductSellingPlanGroups(sellingPlanGroups),
    info: normalizeInfo(arguments[0]),
    available: checkAvailable(variants),
    collections: collections?.nodes,
    onSale: compareAtPriceRange?.minVariantPrice?.amount != 0,
    comparePrice: compareAtPriceRange?.minVariantPrice?.amount || null,
    label: variants ? variants.edges[0]?.node.sku : '',
    options: options
      ? options
          .filter((o) => o.name !== 'Title') // By default Shopify adds a 'Title' name when there's only one option. We don't need it. https://community.shopify.com/c/Shopify-APIs-SDKs/Adding-new-product-variant-is-automatically-adding-quot-Default/td-p/358095
          .map((o) => normalizeProductOption(o))
      : [],
    description: description || '',
    ...(descriptionHtml && { descriptionHtml }),
    ...rest,
  }
}
